import { Drawer, IconButton, Typography, Box, Divider, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface FailedDetail {
  studentName: string;
  subscriptionName: string;
  reason: string;
  phoneNumber:string

}

interface InvoiceResultsDrawerProps {
  open: boolean;
  onClose: () => void;
  results: any
}

export default function InvoiceResultsDrawer({ open, onClose, results }: InvoiceResultsDrawerProps) {
  const handleExportCSV = () => {
    const headers = [
      'Student Name                    ',
      'Phone Number                    ',
      'Subscription Name               ',
      'Failure Reason                  '
    ];

    const formatPhoneNumber = (phone: string) => {
      return phone ? `'${phone}` : 'N/A';
    };

    const csvData = results.failedDetails.map(detail => 
      `"${detail.studentName.padEnd(25)}","${formatPhoneNumber(detail.phoneNumber)}","${detail.subscriptionName.padEnd(25)}","${detail.reason}"`
    );
    
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...csvData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const date = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `failed_invoices_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      
      PaperProps={{
        sx: {
          width: '620px',
          padding: '24px',
          bgcolor: '#FFFFFF',
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          aria-label="Close drawer"
          sx={{
            position: 'absolute',
            right: -8,
            top: -8,
            p: 0.75,
            px: 1,
            color: '#666',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.04)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 3.5, 
            fontWeight: 600,
            fontSize: '0.95rem',
            letterSpacing: '0.01em',
            color: '#2c3e50'
          }}
        >
          Invoice Generation Results
        </Typography>

        <Box 
          sx={{ 
            mb: 3.5, 
            py: 2,
            px: 2.5,
            borderRadius: 1.5,
            bgcolor: '#f1f8f1',
            border: '1px solid',
            borderColor: '#e0eee0',
            display: 'flex', 
            alignItems: 'center', 
            gap: 2
          }}
        >
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
          <Box>
            <Typography sx={{ 
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#1b5e20',
              mb: 0.25,
              display: 'flex',
              alignItems: 'center'
            }}>
              {results.sentCount} invoices queued
            </Typography>
            <Typography sx={{ 
              fontSize: '0.75rem',
              color: '#388e3c',
              letterSpacing: '0.01em'
            }}>
              Successfully processed and will be set shortly.
            </Typography>
          </Box>
        </Box>

        {results.failedDetails.length > 0 ? (
          <>
            <Divider sx={{ 
              my: 3,
              borderColor: 'rgba(0,0,0,0.06)'
            }} />
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2.5,
              px: 0.5
            }}>
              <Typography sx={{ 
                fontSize: '0.8125rem',
                color: '#d32f2f',
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.75,
                fontWeight: 600
              }}>
                <ErrorIcon sx={{ fontSize: 16 }} />
                Invoices Not Sent ({results.failedDetails.length})
              </Typography>
              
              <Button
                variant="text"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
                onClick={handleExportCSV}
                sx={{
                  fontSize: '0.75rem',
                  color: '#1976d2',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 0,
                  p: '4px 12px',
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.04)'
                  }
                }}
              >
                Export
              </Button>
            </Box>

            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              maxHeight: '60vh',
              overflowY: 'auto',
              px: 0.5,
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#ddd',
                borderRadius: '4px',
                '&:hover': {
                  background: '#ccc',
                }
              },
            }}>
              {results.failedDetails.map((detail:any, index:any) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'rgba(211, 47, 47, 0.12)',
                    bgcolor: 'rgba(211, 47, 47, 0.02)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'rgba(211, 47, 47, 0.2)',
                      bgcolor: 'rgba(211, 47, 47, 0.03)',
                    },
                  }}
                >
                  
                  <div className='flex flex-col text-xs'>
                   <span> Student: {detail.studentName}</span>
                    <span> Subscription Name: {detail.subscriptionName}</span>
                  </div>
                  
                  <Typography sx={{ 
                    fontSize: '0.75rem',
                    color: '#d32f2f',
                    py: 1,
                  
                    bgcolor: 'rgba(211, 47, 47, 0.04)',
                    borderRadius: 1,
                    lineHeight: 1.4
                  }}>
                    Reason: {detail.reason}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: '#f1f8f1',
              borderRadius: 1.5,
              border: '1px solid #e0eee0'
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#2e7d32',
                fontWeight: 600,
                mb: 0.5
              }}
            >
              You're good! 🎉
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#388e3c'
              }}
            >
              Invoice raised for everyone successfully.
            </Typography>
          </Box>
        )}
      </Box>
    </Drawer>
  );
} 